import { Button, Checkbox } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { poolMarkInitials } from "../pools/poolsListState";
import {
  buildPickemTieGuessLine,
  buildPickemTieStandingLine,
  CONFIRM_GATE_NAMING_BODY,
  CONFIRM_GATE_NAMING_DISCLOSURE,
  CONFIRM_GATE_NAMING_SAVE_LABEL,
  CONFIRM_GATE_PICKEM_NAMING_BODY,
} from "./confirmGateCopy";
import type { typeConfirmGateEntrant } from "./confirmGateState";

export type typeConfirmGateNamingStepProps = {
  candidates: readonly typeConfirmGateEntrant[];
  /** Called with the checked entry ids, in the order they appear in `candidates`. */
  onSave: (entryIds: readonly string[]) => void;
  isSaving?: boolean;
  /** ISSUE-1309. Words the tie for pick'em; each candidate's line shows either way. */
  isPickem?: boolean;
};

export const ConfirmGateNamingStep = ({
  candidates,
  onSave,
  isSaving = false,
  isPickem = false,
}: typeConfirmGateNamingStepProps): ReactNode => {
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);

  return (
    <div className="flex flex-col gap-3" data-testid="confirm-gate-naming-step">
      <p data-testid="confirm-gate-naming-body">
        {isPickem ? CONFIRM_GATE_PICKEM_NAMING_BODY : CONFIRM_GATE_NAMING_BODY}
      </p>
      {candidates.map((candidate) => (
        <div
          key={candidate.entryId}
          className="flex items-center gap-3"
          data-testid="confirm-gate-candidate-row"
        >
          <span
            aria-hidden="true"
            className="grid size-8 shrink-0 place-items-center rounded-full bg-ink-secondary text-caption font-bold text-ink-inverse"
          >
            {poolMarkInitials(candidate.displayName)}
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <Checkbox
              label={candidate.displayName}
              checked={selectedIds.includes(candidate.entryId)}
              value={candidate.entryId}
              onCheckedChange={(checked) => {
                if (checked === true) {
                  setSelectedIds((current) => [...current, candidate.entryId]);
                } else {
                  setSelectedIds((current) =>
                    current.filter((id) => id !== candidate.entryId),
                  );
                }
              }}
            />
            {candidate.pickemStanding ? (
              <div
                className="flex flex-col text-caption text-ink-secondary"
                data-testid="confirm-gate-candidate-standing"
              >
                <span>
                  {buildPickemTieStandingLine(candidate.pickemStanding)}
                </span>
                <span>{buildPickemTieGuessLine(candidate.pickemStanding)}</span>
              </div>
            ) : null}
          </div>
        </div>
      ))}
      <p data-testid="confirm-gate-naming-disclosure">
        {CONFIRM_GATE_NAMING_DISCLOSURE}
      </p>
      <Button
        variant="primary"
        fullWidth
        disabled={selectedIds.length === 0}
        loading={isSaving}
        loadingLabel="Saving the winner"
        onClick={() => {
          onSave(selectedIds);
        }}
      >
        {CONFIRM_GATE_NAMING_SAVE_LABEL}
      </Button>
    </div>
  );
};
